/**
 * CompleteProfileModal
 *
 * Publish-time profile-completeness gate (Subscription Gating Spec §5.2).
 * Shown from Gallery.js's onSubmit AFTER the ad has already been saved as
 * a draft — the coach never loses their work, they just can't flip the ad
 * to published until the listed items are fixed.
 *
 * Visual language mirrors UpgradeRequiredModal (same gradient card / gold
 * accent / backdrop) so the two gates that can block "Create Ad" / "Submit"
 * read as one system instead of two different popup styles. Pure inline
 * styles, same reason as UpgradeRequiredModal — no stylesheet import
 * order to depend on.
 *
 * Props
 *   isOpen     (boolean) controls visibility
 *   onClose    (() => void) backdrop click, Escape, X, "Maybe later"
 *   onNavigate ((path: string) => void) called when a row's "Fix" is
 *                                       clicked — parent should close the
 *                                       modal and route to `path`
 *   items      (Array<{ icon: Component, title: string, detail?: string,
 *                        path: string }>) the missing-field rows
 */

import React, { useEffect } from 'react';

const CompleteProfileModal = ({ isOpen, onClose, onNavigate, items = [] }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = e => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    onClose && onClose();
  };
  const handleCardClick = e => e.stopPropagation();
  const handleFixClick = path => {
    onClose && onClose();
    onNavigate && onNavigate(path);
  };

  return (
    <>
      <style>{`
        @keyframes completeProfileModalFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes completeProfileModalSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="complete-profile-modal-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          animation: 'completeProfileModalFadeIn 0.25s ease-out',
        }}
      >
        {/* Card */}
        <div
          onClick={handleCardClick}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '520px',
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
            padding: '32px',
            borderRadius: '20px',
            background:
              'radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.32) 0%, rgba(139, 92, 246, 0) 55%), ' +
              'linear-gradient(135deg, #064E3B 0%, #0F3F4A 45%, #1E1040 100%)',
            border: '1px solid rgba(251, 191, 36, 0.35)',
            boxShadow: '0 30px 80px rgba(15, 23, 42, 0.55)',
            color: '#FFFFFF',
            animation: 'completeProfileModalSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Close (X) button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#FFFFFF',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            ×
          </button>

          {/* Eyebrow */}
          <div
            style={{
              display: 'inline-block',
              padding: '6px 14px',
              marginBottom: '18px',
              borderRadius: '999px',
              background: 'rgba(251, 191, 36, 0.18)',
              border: '1px solid rgba(251, 191, 36, 0.45)',
              color: '#FBBF24',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
            }}
          >
            Almost there
          </div>

          {/* Heading */}
          <h2
            id="complete-profile-modal-title"
            style={{
              margin: '0 0 8px',
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: 1.25,
              color: '#FFFFFF',
            }}
          >
            Complete your profile to publish
          </h2>

          {/* Body */}
          <p
            style={{
              margin: '0 0 24px',
              fontSize: '15px',
              lineHeight: 1.55,
              color: 'rgba(255, 255, 255, 0.86)',
            }}
          >
            Your ad is saved as a draft — it just isn't visible to clients yet.{' '}
            {items.length === 1
              ? '1 required detail is'
              : `${items.length} required details are`}{' '}
            missing before it can go live.
          </p>

          {/* Checklist */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
            {items.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '14px 16px',
                    borderRadius: '14px',
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                  }}
                >
                  <div
                    style={{
                      flexShrink: 0,
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: 'rgba(251, 191, 36, 0.16)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FBBF24',
                    }}
                  >
                    {Icon ? <Icon size={16} /> : null}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>
                      {item.title}
                    </div>
                    {item.detail ? (
                      <div style={{ fontSize: '12.5px', color: 'rgba(255, 255, 255, 0.65)', marginTop: '2px' }}>
                        {item.detail}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFixClick(item.path)}
                    style={{
                      flexShrink: 0,
                      appearance: 'none',
                      cursor: 'pointer',
                      padding: '8px 16px',
                      borderRadius: '10px',
                      border: '1px solid rgba(251, 191, 36, 0.45)',
                      background: 'rgba(251, 191, 36, 0.14)',
                      color: '#FBBF24',
                      fontWeight: 700,
                      fontSize: '12.5px',
                    }}
                  >
                    Fix
                  </button>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                appearance: 'none',
                cursor: 'pointer',
                padding: '12px 18px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                background: 'transparent',
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompleteProfileModal;
